/* Bundle savings preview for Metro's "Bundle products" builder.
 *
 * Metro keeps a running subtotal of the picked items; this script watches
 * those picks and shows the bundle saving before the customer reaches the
 * cart. It uses the same rules the checkout discount function uses (the shop
 * metafield custom.bundle_combo_rules, rendered into the section as JSON), so
 * the number shown here is the number Shopify applies at checkout:
 *
 *   - each picked item maps to a type (deck box, sleeves, playmat, field centre)
 *     via the product type Metro renders on the card;
 *   - of the listed combinations that fit inside the picked types, the one
 *     saving the most money wins; its percentage applies to the items of
 *     those types and everything else stays full price (same as the function).
 *
 * Nothing here changes prices -- the real discount is applied by the function
 * at cart/checkout. This is display only, which is why the maths is kept
 * identical to the function's (same normalisation, same key format).
 *
 * It also carries three bits of behaviour that have nowhere better to live,
 * because Metro's bundle engine is in theme-script.js and we do not edit that:
 *
 *   1. Per-option selects. The card renders one select per product option
 *      (Card Size / Finish Style / Number of Sleeves) and hides Metro's flat
 *      list of every variant. We map the chosen values back onto that hidden
 *      select by variant title and fire its change event, so Metro's own code
 *      still resolves the variant, the price and the image.
 *   2. Quantity. Metro has no concept of it: the picked items are just DOM and
 *      the form posts one id[] per pick. We stamp data-product-qty on each
 *      picked row, add a matching quantity[] field next to its id[] (Shopify's
 *      /cart/add accepts index-aligned id[]/quantity[] arrays), and multiply
 *      by it here. The checkout function already discounts line subtotals,
 *      which are price x quantity, so page and checkout agree.
 *   3. Add / remove. Metro's card button only adds. We relabel it to
 *      "Remove from Bundle" when its product is already picked and turn the
 *      click into a removal.
 */
(() => {
  const DEFAULT_RULES = {
    types: { 'deck boxes': 'deckbox', sleeves: 'sleeves', playmat: 'playmat', 'field center': 'fieldcenter' },
    combos: {
      'deckbox+sleeves': 10,
      'deckbox+playmat': 18,
      'deckbox+fieldcenter': 25,
      'playmat+sleeves': 16,
      'deckbox+playmat+sleeves': 23,
      'deckbox+fieldcenter+playmat+sleeves': 28,
    },
  };

  const comboKey = (ids) => [...new Set(ids)].sort().join('+');

  function loadRules(json) {
    const out = { types: {}, combos: {} };
    const src = json && typeof json === 'object' ? json : {};
    const types = src.types && typeof src.types === 'object' ? src.types : DEFAULT_RULES.types;
    for (const [t, id] of Object.entries(types)) {
      if (typeof id === 'string' && id.trim()) out.types[t.trim().toLowerCase()] = id.trim().toLowerCase();
    }
    const combos = src.combos && typeof src.combos === 'object' ? src.combos : DEFAULT_RULES.combos;
    for (const [key, pct] of Object.entries(combos)) {
      const v = Number(pct);
      if (Number.isFinite(v) && v > 0 && v <= 100) {
        const ids = key.toLowerCase().split('+').map((s) => s.trim()).map((s) => out.types[s] || s);
        out.combos[comboKey(ids)] = v;
      }
    }
    return out;
  }

  function formatMoney(cents) {
    try {
      if (window.theme && theme.Currency && theme.settings) {
        return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
      }
    } catch (e) { /* fall through */ }
    return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'CAD' });
  }

  function setup(section) {
    if (section.dataset.bundleSavingsReady) return;
    section.dataset.bundleSavingsReady = '1';

    const rulesEl = section.querySelector('[data-bundle-rules]');
    let rules;
    try { rules = loadRules(JSON.parse(rulesEl ? rulesEl.textContent : 'null')); } catch (e) { rules = loadRules(null); }

    // product id -> type id, from the cards Metro renders
    const typeOf = {};
    section.querySelectorAll('bundle-card[data-product-id]').forEach((card) => {
      const t = String(card.dataset.productType || '').trim().toLowerCase();
      if (rules.types[t]) typeOf[card.dataset.productId] = rules.types[t];
    });

    const rows = section.querySelector('[data-bundle-savings]');
    const subtotalEl = section.querySelector('[data-bundle-subtotal]');
    const labelEl = section.querySelector('[data-bundle-savings-label]');
    const amountEl = section.querySelector('[data-bundle-savings-amount]');
    const totalEl = section.querySelector('[total-item-bundle-amount]');
    const totalBtnEl = section.querySelector('[total-item-bundle-amount-btn]');
    const hintEl = section.querySelector('[data-bundle-savings-hint]');
    const items = section.querySelector('.bundle-product-cart-items');
    if (!rows || !totalEl || !items) return;

    // Cheapest next step: which single type, added to what is picked, gives the
    // best listed combination. Shown as a nudge when the current pick has no
    // saving or a better one is one item away.
    function bestNext(subtotalByType, currentSaving) {
      let best = null;
      // Only types this set actually offers, so a set without a field centre
      // is never told to add one. Price of the missing item comes from its card.
      for (const card of section.querySelectorAll('bundle-card[data-product-id]')) {
        const id = typeOf[card.dataset.productId];
        if (!id || id in subtotalByType) continue;
        const opt = card.querySelector('[data-bundle-variant-select] option:not([disabled])');
        const price = opt ? parseFloat(opt.dataset.variantPrice) || 0 : 0;
        const b = bestCombo({ ...subtotalByType, [id]: price });
        if (b && b.saving > currentSaving && (!best || b.saving > best.saving)) best = { id, pct: b.pct, saving: b.saving };
      }
      return best;
    }
    // Same selection as the checkout function: best money saving among the
    // listed combinations made only of picked types; ties to the bigger one.
    function bestCombo(subtotalByType) {
      const present = new Set(Object.keys(subtotalByType));
      let best = null;
      for (const [key, pct] of Object.entries(rules.combos)) {
        const types = key.split('+');
        if (!types.every((t) => present.has(t))) continue;
        const saving = types.reduce((sum, t) => sum + subtotalByType[t], 0) * pct / 100;
        if (!best || saving > best.saving || (saving === best.saving && types.length > best.types.size)) {
          best = { types: new Set(types), pct, saving };
        }
      }
      return best;
    }
    const typeLabel = (id) => {
      const entry = Object.entries(rules.types).find(([, v]) => v === id);
      return entry ? entry[0].replace(/\b\w/g, (c) => c.toUpperCase()).replace('Deck Boxes', 'Deck box') : id;
    };

    const clampQty = (n) => Math.min(10, Math.max(1, parseInt(n, 10) || 1));
    const qtyOf = (row) => clampQty(row.dataset.productQty || 1);

    // Quantity chosen on the card at the moment Add to bundle was clicked.
    // Captured in the capture phase so it is known before Metro injects the row.
    let pendingQty = 1;

    // One per card: re-asserts whether its CTA may be clicked. Metro re-enables
    // buttons of its own accord when the bundle changes, so these run again
    // after every add and remove.
    const gates = [];

    // One select per product option, driving Metro's hidden flat select.
    function enhanceCard(card) {
      if (card.dataset.bundleOptionsReady) return;
      card.dataset.bundleOptionsReady = '1';

      const addBtn = card.querySelector('[button-add-to-bundle]');
      if (addBtn) {
        addBtn.addEventListener('click', () => {
          const input = card.querySelector('[data-bundle-qty]');
          pendingQty = clampQty(input ? input.value : 1);
        }, true);
      }

      card.querySelectorAll('[data-bundle-qty-step]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const input = card.querySelector('[data-bundle-qty]');
          if (!input) return;
          input.value = clampQty(Number(input.value) + Number(btn.dataset.bundleQtyStep));
        });
      });

      const flat = card.querySelector('[data-bundle-variant-select]');
      const optionSelects = [...card.querySelectorAll('[data-bundle-option-select]')];
      if (!flat) return;

      const note = card.querySelector('[data-bundle-variant-note]');

      // Every card now opens with nothing chosen where a choice matters -- the
      // deck box's Color, the sleeves' size and finish -- so the CTA has to
      // stay closed until there is a real variant behind it. Metro's own button
      // would happily add whatever the select happened to be showing.
      const hasVariant = () => {
        if (optionSelects.length) {
          if (optionSelects.some((s) => s.value === '')) return false;
          // Shopify variant titles are the option values joined by " / ", which
          // is what data-variant-name holds -- no extra variant map needed.
          const title = optionSelects.map((s) => s.value).join(' / ');
          const match = [...flat.options].find((o) => (o.dataset.variantName || '') === title);
          return !!match && !match.disabled;
        }
        const opt = flat.options[flat.selectedIndex];
        return !!opt && !opt.disabled && !!opt.value;
      };

      const gate = () => {
        if (!addBtn || addBtn.hasAttribute('data-bundle-limit-disabled')) return;
        // A product already in the bundle needs its button live to remove it.
        if (addBtn.classList.contains('is-in-bundle')) return;
        addBtn.disabled = !hasVariant();
      };
      gates.push(gate);
      flat.addEventListener('change', gate);

      // One option: Metro's own select is the control, nothing to mirror.
      if (!optionSelects.length) {
        gate();
        return;
      }

      const sync = () => {
        // An unanswered placeholder is not an error, so no note for it -- the
        // disabled CTA and the empty select say enough.
        if (optionSelects.some((s) => s.value === '')) {
          if (note) {
            note.textContent = '';
            note.hidden = true;
          }
          gate();
          return;
        }
        const title = optionSelects.map((s) => s.value).join(' / ');
        const match = [...flat.options].find((o) => (o.dataset.variantName || '') === title);
        const ok = !!match && !match.disabled;
        if (ok) {
          flat.value = match.value;
          flat.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (note) {
          note.textContent = ok ? '' : (match ? 'Sold out in this combination' : 'Not available in this combination');
          note.hidden = ok;
        }
        gate();
      };
      optionSelects.forEach((s) => s.addEventListener('change', sync));
      sync();
    }

    // The card CTA doubles as a remove control. Metro only knows how to add, so
    // when this product is already picked we relabel the button and turn the
    // click into a click on that row's close button instead.
    const CTA_ADD = 'Add to Bundle';
    const CTA_REMOVE = 'Remove from Bundle';

    function syncCtas() {
      const picked = new Set(
        [...section.querySelectorAll('[have-product-data]')].map((el) => el.dataset.productId)
      );
      section.querySelectorAll('bundle-card[data-product-id]').forEach((card) => {
        const btn = card.querySelector('[button-add-to-bundle]');
        if (!btn) return;
        const inBundle = picked.has(card.dataset.productId);
        btn.classList.toggle('is-in-bundle', inBundle);
        const label = btn.querySelector('[data-bundle-cta-label]');
        const text = inBundle ? CTA_REMOVE : CTA_ADD;
        if (label && label.textContent.trim() !== text) label.textContent = text;
        btn.setAttribute('aria-label', text);
        // Metro disables the button once the product is in the bundle (or the
        // bundle is full); removing has to stay available either way.
        if (inBundle) {
          btn.disabled = false;
          btn.removeAttribute('data-bundle-limit-disabled');
        }
      });
    }

    // Capture on the section so this runs before Metro's own listener on the
    // button, which would otherwise add a second copy of the product.
    section.addEventListener('click', (event) => {
      const btn = event.target.closest('[button-add-to-bundle]');
      if (!btn || !btn.classList.contains('is-in-bundle')) return;
      event.preventDefault();
      event.stopPropagation();
      const card = btn.closest('bundle-card');
      if (!card) return;
      const row = [...section.querySelectorAll('[have-product-data]')]
        .find((el) => el.dataset.productId === card.dataset.productId);
      const close = row && row.querySelector('.bundle-cart-close');
      if (close) close.click();
    }, true);

    // Metro injects picked rows as raw HTML and shifts that HTML between slots
    // when one is removed, so quantity lives in the markup (attribute + hidden
    // input) and the stepper is delegated -- listeners would not survive a shift.
    function stampRows() {
      section.querySelectorAll('[have-product-data]').forEach((row) => {
        if (row.dataset.productQty) {
          const field = row.querySelector('input[name="quantity[]"]');
          if (field) field.value = qtyOf(row);
          return;
        }
        row.dataset.productQty = String(clampQty(pendingQty));
        pendingQty = 1;

        const idInput = row.querySelector('input[name="id[]"]');
        if (idInput && !row.querySelector('input[name="quantity[]"]')) {
          const field = document.createElement('input');
          field.type = 'hidden';
          field.name = 'quantity[]';
          field.value = row.dataset.productQty;
          idInput.insertAdjacentElement('afterend', field);
        }

        if (!row.querySelector('[data-bundle-row-qty]')) {
          const box = document.createElement('div');
          box.className = 'bundle-row-qty';
          box.setAttribute('data-bundle-row-qty', '');
          box.innerHTML =
            '<button type="button" class="bundle-qty-btn" data-bundle-row-step="-1" aria-label="Decrease quantity">&minus;</button>' +
            '<span class="bundle-row-qty-value" data-bundle-row-qty-value></span>' +
            '<button type="button" class="bundle-qty-btn" data-bundle-row-step="1" aria-label="Increase quantity">+</button>';
          const host = row.querySelector('.horizontal-product-card-content') || row;
          host.appendChild(box);
        }
      });
      section.querySelectorAll('[have-product-data]').forEach((row) => {
        const value = row.querySelector('[data-bundle-row-qty-value]');
        const text = `x${qtyOf(row)}`;
        if (value && value.textContent !== text) value.textContent = text;
      });
    }

    section.addEventListener('click', (event) => {
      const step = event.target.closest('[data-bundle-row-step]');
      if (!step) return;
      const row = step.closest('[have-product-data]');
      if (!row) return;
      event.preventDefault();
      row.dataset.productQty = String(clampQty(qtyOf(row) + Number(step.dataset.bundleRowStep)));
      const field = row.querySelector('input[name="quantity[]"]');
      if (field) field.value = row.dataset.productQty;
      const value = row.querySelector('[data-bundle-row-qty-value]');
      if (value) value.textContent = `x${qtyOf(row)}`;
      recalc();
    });

    function recalc() {
      const picked = [...section.querySelectorAll('[have-product-data]')];
      let subtotal = 0;
      const subtotalByType = {};
      for (const el of picked) {
        const price = parseFloat(el.dataset.productPrice) || 0;
        const qty = qtyOf(el);
        subtotal += price * qty;
        const id = typeOf[el.dataset.productId];
        if (id) subtotalByType[id] = (subtotalByType[id] || 0) + price * qty;
      }
      const best = picked.length ? bestCombo(subtotalByType) : null;

      if (best) {
        const saving = Math.round(best.saving);
        const covered = picked.filter((el) => best.types.has(typeOf[el.dataset.productId])).length;
        subtotalEl.textContent = formatMoney(subtotal);
        labelEl.textContent = covered < picked.length
          ? `Bundle savings (${best.pct}% on ${[...best.types].map(typeLabel).join(' + ')})`
          : `Bundle savings (${best.pct}%)`;
        amountEl.textContent = `-${formatMoney(saving)}`;
        rows.hidden = false;
        const total = formatMoney(subtotal - saving);
        if (totalEl.textContent !== total) totalEl.textContent = total;
        if (totalBtnEl) totalBtnEl.textContent = `${total}:`;
      } else {
        rows.hidden = true;
        // Metro's own subtotal ignores quantity, so write it ourselves rather
        // than leaving a figure that is wrong the moment a qty is above 1.
        const total = formatMoney(subtotal);
        if (totalEl.textContent !== total) totalEl.textContent = total;
        if (totalBtnEl) totalBtnEl.textContent = `${total}:`;
      }

      if (hintEl) {
        const next = picked.length ? bestNext(subtotalByType, best ? best.saving : 0) : null;
        if (next) {
          hintEl.textContent = `Add the ${typeLabel(next.id)} to save ${next.pct}%`;
          hintEl.hidden = false;
        } else if (picked.length && !best) {
          hintEl.textContent = 'No bundle saving for this combination';
          hintEl.hidden = false;
        } else {
          hintEl.hidden = true;
        }
      }
    }

    // Metro rewrites the item slots on every add/remove/clear; stamp quantity
    // onto any newly injected row, then recalc.
    //
    // stampRows() writes INTO the container this observer watches (it adds the
    // quantity field and the stepper), so every stamp would re-enter the
    // callback and spin forever. Disconnect for the duration of our own writes
    // and take the records that piled up in the meantime as already handled.
    const observer = new MutationObserver(handleMutations);
    function handleMutations() {
      observer.disconnect();
      try {
        stampRows();
        syncCtas();
        gates.forEach((gate) => gate());
        recalc();
      } finally {
        observer.takeRecords();
        observer.observe(items, { childList: true, subtree: true });
      }
    }
    observer.observe(items, { childList: true, subtree: true });
    section.querySelectorAll('bundle-card[data-product-id]').forEach(enhanceCard);
    stampRows();
    syncCtas();
    gates.forEach((gate) => gate());
    recalc();
  }

  const init = () => document.querySelectorAll('bundle-products-card').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
